import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const hasProcessed = useRef(false);
  
  const [status, setStatus] = React.useState('loading'); // loading, success, error
  const [errorMessage, setErrorMessage] = React.useState('');

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage('Invalid verification link');
        return;
      }

      try {
        const response = await fetch(`${API}/api/auth/verify-email?token=${token}`, {
          method: 'POST'
        });

        const text = await response.text();
        let data;
        try {
          data = text ? JSON.parse(text) : {};
        } catch (e) {
          throw new Error('Invalid server response');
        }

        if (!response.ok) {
          throw new Error(data.detail || 'Verification failed');
        }

        setStatus('success');
        toast.success('Email verified successfully!');
      } catch (error) {
        setStatus('error');
        setErrorMessage(error.message);
      }
    };

    verifyEmail();
  }, [token]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="verify-email-loading">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-display font-semibold mb-3">Verifying your email...</h1>
          <p className="text-muted-foreground">Please wait while we verify your email address.</p>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="verify-email-error">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-display font-semibold mb-3">Verification Failed</h1>
          <p className="text-muted-foreground mb-6">{errorMessage}</p>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/login')} 
              className="w-full"
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/signup')} 
              className="w-full"
              data-testid="try-again-btn"
            >
              Sign Up Again
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="verify-email-success">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full text-center"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h1 className="text-2xl font-display font-semibold mb-3">Email Verified!</h1>
        <p className="text-muted-foreground mb-6">
          Your email has been verified successfully. You can now log in to your account.
        </p>
        <Button 
          onClick={() => navigate('/login')} 
          className="w-full"
          data-testid="go-to-login-btn"
        >
          Go to Login
        </Button>
      </motion.div>
    </div>
  );
};

export default VerifyEmail;
